import React, {useState} from 'react'
import {
  ColumnDef as BaseColumnDef,
  Column as BaseColumn,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table'
import {FilterAnalytics} from '../Analytics/Component/FilterAnalytics'
import clsx from 'clsx'
import {useIntl} from 'react-intl'

// Tipe tambahan untuk align
type Align = 'left' | 'center' | 'right'

// Tipe meta kustom
type CustomMeta = {
  align?: Align
}

// Custom column definition yang menyertakan meta
type CustomColumnDef<TData> = BaseColumnDef<TData, unknown> & {
  meta?: CustomMeta
}

// Custom column agar cell dan header bisa baca meta
type CustomColumn<TData> = BaseColumn<TData, unknown> & {
  columnDef: {
    meta?: CustomMeta
  }
}

type ModalTableProps<TData> = {
  getPinningStyles?: (column: CustomColumn<TData>, isHeader: boolean) => React.CSSProperties
  renderCustomCell?: (cellValue: any, cell: CustomColumn<TData>) => React.ReactNode
  usePagination?: boolean
  data: TData[]
  columns: CustomColumnDef<TData>[]
  enableGlobalSearch?: boolean
  enableColumnFilter?: boolean
  stickyHeader?:boolean

}

export default function TableLite<TData>({
  getPinningStyles = () => ({}),
  renderCustomCell,
  usePagination,
  data,
  columns,
  enableColumnFilter = false,
  stickyHeader = false
}: ModalTableProps<TData>) {
  const intl = useIntl()
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: usePagination ? 10 : 30,
  })
  const [showDrawer, setShowDrawer] = useState(false)
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState<string>('')

  const table = useReactTable({
    data,
    columns,
    state: {
      pagination,
      sorting,
      columnFilters,
      globalFilter,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
  })

  const resetFilters = () => {
    setColumnFilters([])
    setGlobalFilter('')
    table.resetColumnFilters() // opsional
    table.resetGlobalFilter() // opsional
  }

  const getAlignClass = (align?: Align) => {
    switch (align) {
      case 'left':
        return 'text-start'
      case 'right':
        return 'text-end'
      case 'center':
      default:
        return 'text-center'
    }
  }

  return (
    <>
      <div className='table-responsive' style={{paddingBottom:"62px"}} >
        <div className='d-flex flex-wrap  align-items-end mb-3 gap-3 justify-content-end'>
          {enableColumnFilter && (
            <div className='d-flex gap-2' style={{position:"absolute"}}>
              <button
                className={clsx(
                  'btn text-primary btn-sm ',
                  columnFilters.length > 0 && 'border border-primary'
                )}
                onClick={() => setShowDrawer(true)}
              >
                {intl.formatMessage({id:"FORM.LABEL.FILTER"})}
              </button>

              {(columnFilters.length > 0 || globalFilter) && (
                <button className='btn btn-primary btn-sm' onClick={resetFilters}>
                  Reset Filter
                </button>
              )}
            </div>
          )}
        </div>

        <table
          className='table table-hover table-borderless align-middle'
          style={{backgroundColor: 'white', borderRadius: '8px',
                   ...(stickyHeader
            ? {
              maxHeight:"600px",
              display:"block",
              overflow:"auto"
              }
            : {}),
          }}
        >
          <thead style={{backgroundColor: '#e9f3ff', color: '#0d6efd',
                  ...(stickyHeader
            ? {
                position: 'sticky',
                top: 0,
                zIndex: 1, // optional: supaya tetap di atas saat scroll
              }
            : {}),
          }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const align = (header.column.columnDef.meta as any)?.align
                  return (
                    <th
                      key={header.id}
                      className={`align-middle fw-semibold ${getAlignClass(align)}`}
                      onClick={header.column.getToggleSortingHandler()}
                      {...{
                        style: {
                          cursor: header.column.getCanSort() ? 'pointer' : 'default',
                          padding: '12px 16px',
                          backgroundColor: '#e9f3ff',
                          width: header.getSize(),
                          minWidth: header.getSize(),
                          maxWidth: header.getSize(),
                          color: '#000',
                          ...getPinningStyles(header.column as CustomColumn<TData>, true),
                        },
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : typeof header.column.columnDef.header === 'function'
                        ? header.column.columnDef.header(header.getContext())
                        : header.column.columnDef.header}
                      {header.column.getIsSorted() === 'asc' && <span className='ms-1'>↑</span>}
                      {header.column.getIsSorted() === 'desc' && <span className='ms-1'>↓</span>}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  const align = (cell.column.columnDef.meta as any)?.align
                  return (
                    <td
                      key={cell.id}
                      className={`align-middle text-dark ${getAlignClass(align)}`}
                      {...{
                        style: {
                          width: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                          minWidth: cell.column.getSize(),
                          padding: '12px 16px',
                          backgroundColor: 'white',
                          ...getPinningStyles(cell.column as CustomColumn<TData>, false),
                        },
                      }}
                    >
                      {renderCustomCell
                        ? renderCustomCell(cell.getValue(), cell.column as CustomColumn<TData>)
                        : typeof cell.column.columnDef.cell === 'function'
                        ? cell.column.columnDef.cell(cell.getContext())
                        : cell.column.columnDef.cell}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {usePagination && (
          <div className='d-flex justify-content-end align-items-center flex-wrap pt-3 pb-3' style={{position:"absolute", right:"36px"}}>
            <div className='d-flex align-items-center gap-3'>
              <select
                className='form-select form-select-sm bg-white border'
                style={{width: '75px'}}
                value={table.getState().pagination.pageSize}
                onChange={(e) => table.setPageSize(Number(e.target.value))}
              >
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>
              <span className='small text-dark'>
                {pagination.pageIndex * pagination.pageSize + 1} -{' '}
                {Math.min(
                  (pagination.pageIndex + 1) * pagination.pageSize,
                  table.getFilteredRowModel().rows.length
                )}{' '}
                of {table.getFilteredRowModel().rows.length}
              </span>

              <nav>
                <ul className='pagination mb-0'>
                  <li className={`page-item ${!table.getCanPreviousPage() ? 'disabled' : ''}`}>
                    <button
                      className='page-link'
                      onClick={() => table.setPageIndex(0)}
                      disabled={!table.getCanPreviousPage()}
                    >
                      «
                    </button>
                  </li>
                  <li className={`page-item ${!table.getCanPreviousPage() ? 'disabled' : ''}`}>
                    <button
                      className='page-link'
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      ‹
                    </button>
                  </li>
                  <li className={`page-item ${!table.getCanNextPage() ? 'disabled' : ''}`}>
                    <button
                      className='page-link'
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      ›
                    </button>
                  </li>
                  <li className={`page-item ${!table.getCanNextPage() ? 'disabled' : ''}`}>
                    <button
                      className='page-link'
                      onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                      disabled={!table.getCanNextPage()}
                    >
                      »
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>

      <FilterAnalytics
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        table={table}
        data={data}
      />
    </>
  )
}